export class NotificationResponseContractError extends Error {
  constructor(operation, detail) {
    super(`Invalid ${operation} response: ${detail}`);
    this.name = 'NotificationResponseContractError';
    this.code = 'INVALID_API_RESPONSE';
  }
}

const isRecord = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const optionalText = value => value == null || typeof value === 'string';
const optionalDate = value => value == null || typeof value === 'string' && Number.isFinite(Date.parse(value));
const isReference = value => typeof value === 'string' && Boolean(value) || isRecord(value) && typeof value._id === 'string' && Boolean(value._id);
const optionalReference = value => value == null || isReference(value);
const targets = new Set(['Activity', 'Event', 'Media', 'User', 'Post', 'Session', 'VenueRequest']);
const scopes = new Set(['personal', 'community', 'multi-community']);
const isUser = value => optionalReference(value) && (!isRecord(value)
  || ['firstName', 'lastName', 'profilePicture'].every(field => optionalText(value[field])));
const isType = value => value == null || typeof value === 'string' && Boolean(value)
  || isRecord(value) && (value._id === undefined || isReference(value))
    && typeof value.typeName === 'string' && Boolean(value.typeName) && optionalText(value.description);
const isAdditionalData = value => value === undefined || isRecord(value)
  && ['caption', 'message', 'expenseId', 'contributionId', 'treasuryCommunityId'].every(field => optionalText(value[field]));
const isReceiptList = (value, timestamp) => value === undefined || Array.isArray(value)
  && value.every(receipt => isRecord(receipt) && isReference(receipt.user) && optionalDate(receipt[timestamp]));

export function readNotificationRecordResponse(payload, operation = 'notification') {
  if (!isRecord(payload) || typeof payload._id !== 'string' || !payload._id) {
    throw new NotificationResponseContractError(operation, 'expected a notification with a non-empty string _id.');
  }
  if (!isType(payload.type) || !isUser(payload.actor) || !isUser(payload.recipient)
    || !optionalReference(payload.recipientCommunity) || !optionalReference(payload.target)
    || payload.targetType != null && !targets.has(payload.targetType)
    || payload.scope !== undefined && !scopes.has(payload.scope)
    || payload.isRead !== undefined && typeof payload.isRead !== 'boolean'
    || !isAdditionalData(payload.additionalData)
    || !['createdAt', 'updatedAt'].every(field => optionalDate(payload[field]))
    || !isReceiptList(payload.readBy, 'readAt') || !isReceiptList(payload.unreadBy, 'unreadAt')) {
    throw new NotificationResponseContractError(operation, 'invalid notification relationships, data or timestamps.');
  }
  if (isRecord(payload.target)
    && (!['firstName', 'lastName', 'title', 'name'].every(field => optionalText(payload.target[field]))
      || !['date', 'startTime', 'endTime'].every(field => optionalDate(payload.target[field])))) {
    throw new NotificationResponseContractError(operation, 'invalid populated target summary.');
  }
  return payload;
}

export function readNotificationListResponse(payload) {
  if (!isRecord(payload) || payload.success !== true || !Array.isArray(payload.data)) {
    throw new NotificationResponseContractError('notification list', 'expected { success: true, data: Notification[] }.');
  }
  payload.data.forEach((item) => {
    readNotificationRecordResponse(item, 'notification list item');
    if (typeof item.isRead !== 'boolean') {
      throw new NotificationResponseContractError('notification list item', 'expected isRead: boolean.');
    }
  });
  return payload;
}

export function readNotificationMutationResponse(payload, operation = 'notification mutation', expectedRead = null) {
  if (!isRecord(payload) || payload.success !== true) {
    throw new NotificationResponseContractError(operation, 'expected { success: true, data: Notification }.');
  }
  readNotificationRecordResponse(payload.data, operation);
  if (typeof payload.data.isRead !== 'boolean' || expectedRead !== null && payload.data.isRead !== expectedRead) {
    throw new NotificationResponseContractError(operation, 'expected the resulting isRead state.');
  }
  return payload;
}

export function readNotificationMarkAllResponse(payload) {
  if (!isRecord(payload) || payload.success !== true
    || typeof payload.message !== 'string' || !payload.message
    || !Number.isInteger(payload.updatedCount) || payload.updatedCount < 0) {
    throw new NotificationResponseContractError('mark all notifications read', 'expected { success: true, message, updatedCount }.');
  }
  return payload;
}

export function readNotificationUnreadCountResponse(payload) {
  if (!isRecord(payload) || payload.success !== true
    || !Number.isInteger(payload.count) || payload.count < 0) {
    throw new NotificationResponseContractError('unread notification count', 'expected { success: true, count }.');
  }
  return payload;
}

export function readNotificationDeleteResponse(payload) {
  if (!isRecord(payload) || payload.success !== true
    || typeof payload.message !== 'string' || !payload.message) {
    throw new NotificationResponseContractError('notification deletion', 'expected { success: true, message }.');
  }
  return payload;
}
